import React, { useEffect } from 'react';
import { X, ExternalLink, Github, Smartphone } from 'lucide-react';
import { Project } from '../types/project';

interface ProjectDetailModalProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      web: '웹',
      app: '모바일 앱',
      backend: '백엔드',
      ai: 'AI/ML',
      blockchain: '블록체인',
      other: '기타'
    };
    return labels[category] || category;
  };

  const getLinkIcon = (type: string) => {
    switch (type) {
      case 'github':
        return <Github className="w-5 h-5" />;
      case 'playstore':
      case 'appstore':
        return <Smartphone className="w-5 h-5" />;
      default:
        return <ExternalLink className="w-5 h-5" />;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop bg-black/80"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-neutral-300 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-neutral-100 hover:bg-black text-black hover:text-white transition-all border border-neutral-300 hover:border-black"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="p-8 border-b border-neutral-200">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-16 h-16 rounded-2xl border border-neutral-300 flex items-center justify-center bg-black text-white flex-shrink-0">
              {project.icon}
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-black mb-2">{project.title}</h2>
              <div className="flex flex-wrap items-center gap-3">
                <span className="px-3 py-1 rounded-full bg-black text-white text-sm font-semibold">
                  {getCategoryLabel(project.category)}
                </span>
                {project.year && (
                  <span className="text-neutral-500 text-sm">{project.year}</span>
                )}
              </div>
            </div>
          </div>

          {/* Links in Header */}
          {project.links && project.links.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-4">
              {project.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white font-semibold hover:bg-neutral-800 transition-all text-sm"
                >
                  {getLinkIcon(link.type)}
                  <span>{link.label || link.type.charAt(0).toUpperCase() + link.type.slice(1)}</span>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Images Gallery */}
        {project.images && project.images.length > 0 && (
          <div className="p-8 border-b border-neutral-200">
            <h3 className="text-xl font-bold text-black mb-4">프로젝트 이미지</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {project.images.map((image, index) => (
                <div key={index} className="rounded-lg overflow-hidden border border-neutral-300 bg-neutral-50">
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-64 object-cover"
                  />
                  {image.caption && (
                    <p className="p-3 text-sm text-neutral-600">{image.caption}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        <div className="p-8 border-b border-neutral-200">
          <h3 className="text-xl font-bold text-black mb-4">프로젝트 설명</h3>
          <p className="text-neutral-700 leading-relaxed whitespace-pre-line">
            {project.fullDescription}
          </p>
        </div>

        {/* Tech Stack */}
        <div className="p-8 border-b border-neutral-200">
          <h3 className="text-xl font-bold text-black mb-4">기술 스택</h3>
          <div className="space-y-4">
            {project.techStack.map((stack, index) => (
              <div key={index} className="bg-neutral-50 p-4 rounded-lg border border-neutral-200">
                <h4 className="text-black font-semibold mb-3">{stack.category}</h4>
                <div className="flex flex-wrap gap-2">
                  {stack.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-3 py-1.5 rounded-full bg-white text-neutral-700 text-sm border border-neutral-300 hover:border-black hover:text-black transition-colors"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div className="p-8">
            <h3 className="text-xl font-bold text-black mb-4">태그</h3>
            <div className="flex flex-wrap gap-2">
              {project.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full bg-neutral-100 text-neutral-600 text-sm border border-neutral-300"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetailModal;
